import { useCallback, useMemo } from 'react';
import moment, { Moment } from 'moment';
import momentTimezone from 'moment-timezone';
import { DATE_FORMAT } from './constants';
import useDateTimeService from './useDateTimeService';
// temporary solution

const getMomentDateObj = (...props: moment.MomentInput[]) => moment(...props);

/**
 * Method for getting date as utc moment object
 * @param date - utc date in the string format
 * @returns {moment.Moment} = moment object
 */
const getMomentDateObjAsUtc = (date: moment.MomentInput) => {
  return moment.utc(date);
};

/**
 * @param date - text date
 * @param format - date format like YYYY-MM-DD
 * @returns {string|null} - text date converted to passed format
 */
const formatDate = (date: any, format: string | undefined) => {
  if (!date) return null;

  return ` ${getMomentDateObj(date).format(format)}`;
};

const isIsoDate = (text: string): boolean => moment(text, DATE_FORMAT.INPUT_VALUE, true).isValid();

const todayIsoDate = (): string => moment().format(DATE_FORMAT.API_ONLY_DATE);

/**
 * @param dateFrom - text date in format 'YYYY-MM-DD HH:mm:ss'
 * @param dateTo - text date in format 'YYYY-MM-DD HH:mm:ss'
 * @returns {string|null} - string duration between two month in format `${year} year, ${month} month, ${day} days`
 */
const getStringDurationBetweenTwoDates = (
  dateFrom: moment.MomentInput,
  dateTo: moment.MomentInput,
  shortValue = false,
) => {
  if (!dateFrom || !dateTo) {
    return null;
  }

  const start = getMomentDateObj(dateFrom, DATE_FORMAT.API).startOf('day');
  const end = getMomentDateObj(dateTo, DATE_FORMAT.API).endOf('day').add(1, 'minute');

  const years = end.diff(start, 'year');
  start.add(years, 'years');
  const months = end.diff(start, 'months');
  start.add(months, 'months');
  const days = end.diff(start, 'days');
  start.add(days, 'days');

  const durationValues = {
    years,
    months,
    days,
  };

  let result = '';
  const sign = end.isAfter(start) ? '' : '- ';
  Object.keys(durationValues).forEach(key => {
    const rawValue = durationValues[key as keyof typeof durationValues];

    if (rawValue) {
      const displayedValue = rawValue < 0 ? -rawValue : rawValue;
      const durationName = shortValue ? key.charAt(0) : rawValue === 1 ? key.replace('s', '') : key;

      result += result.length ? `, ${displayedValue} ${durationName}` : `${displayedValue} ${durationName}`;
    }
  });

  return `${sign}${result}`;
};

const datesDifference = (
  startDate: Moment | undefined,
  endDate: moment.MomentInput,
  hideAgo = false,
  showIn = false,
) => {
  if (!endDate || !startDate) {
    return null;
  }

  const start = moment(startDate);
  const end = moment(endDate);

  const isPast = end.isBefore(start);
  const from = isPast ? end : start;
  const to = isPast ? start : end;

  const years = to.diff(from, 'years');
  const months = to.diff(from.clone().add(years, 'years'), 'months');
  const days = to.diff(from.clone().add(years, 'years').add(months, 'months'), 'days');

  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0) parts.push(`${days}d`);

  if (parts.length === 0) parts.push('0d');

  let result = parts.join(' ');
  if (isPast && !hideAgo) {
    result += ' ago';
  }

  if (!isPast && showIn) {
    result = 'in ' + result;
  }

  return result;
};

/**
 * @param startDate - base date for comparison
 * @param endDate - compared date
 * @param hideAgo - hides trailing "ago" for past dates
 * @param showIn - adds leading "in" for future dates
 * @returns {string|null} - relative difference with expanded units (e.g. "2 days ago")
 */
const datesDifferenceExpanded = (
  startDate: Moment | undefined,
  endDate: moment.MomentInput,
  hideAgo = false,
  showIn = false,
) => {
  const relativeValue = datesDifference(startDate, endDate, hideAgo, showIn);
  if (!relativeValue) return relativeValue;

  return relativeValue.replace(/(\d+)([ymd])\b/g, (_, amountText: string, unit: string) => {
    const amount = Number(amountText);
    const unitMap: Record<string, [string, string]> = {
      y: ['year', 'years'],
      m: ['month', 'months'],
      d: ['day', 'days'],
    };
    const [single, plural] = unitMap[unit] ?? [unit, unit];
    return `${amount} ${amount === 1 || amount === 0 ? single : plural}`;
  });
};

/**
 * @param format - date format like YYYY-MM-DD, 'YYYY-MM-DD' by default
 * @returns moment.Moment - moment object with today date converted to passed format
 */
const getCurrentDate = (format = DATE_FORMAT.INPUT_VALUE) => moment.utc(moment.utc().format(format));

/**
 * @param dateTo - text date
 * @param dateFrom - text date, today date by default
 * @param format - date format like YYYY-MM-DD, 'YYYY-MM-DD' by default
 * @returns {number|null} - month diff between dates
 */
const getMonthsBetween = (
  dateTo: moment.MomentInput,
  dateFrom = getCurrentDate(),
  format = DATE_FORMAT.INPUT_VALUE,
) => {
  if (!dateTo) {
    return null;
  }

  const comparedDate = moment.utc(moment.utc(dateTo).format(format));

  return comparedDate.diff(moment.utc(moment.utc(dateFrom).format(format)), 'months', true);
};

const getDaysBetween = (dateTo: moment.MomentInput, dateFrom: moment.MomentInput, format = DATE_FORMAT.INPUT_VALUE) => {
  const comparedDate = moment.utc(moment.utc(dateTo).format(format));

  return comparedDate.diff(moment.utc(moment.utc(dateFrom).format(format)), 'days');
};

const getHoursBetween = (dateTo: moment.MomentInput, dateFrom: moment.MomentInput, format = DATE_FORMAT.API) => {
  const comparedDate = moment.utc(moment.utc(dateTo).format(format));

  return comparedDate.diff(moment.utc(moment.utc(dateFrom).format(format)), 'hours');
};

const getMinutesBetween = (dateTo: moment.MomentInput, dateFrom: moment.MomentInput, format = DATE_FORMAT.API) => {
  const comparedDate = moment.utc(moment.utc(dateTo).format(format));

  return comparedDate.diff(moment.utc(moment.utc(dateFrom).format(format)), 'minutes');
};

const getMinutesFromDateTime = (date?: moment.MomentInput) => {
  const value = date ? moment(date) : moment();

  return value.hours() * 60 + value.minutes();
};

const formatMinutesAsTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const setTimeFromMinutes = (minutes: number, date?: moment.MomentInput) => {
  const value = date ? moment(date) : moment();

  return value
    .clone()
    .hours(Math.floor(minutes / 60))
    .minutes(minutes % 60)
    .seconds(0)
    .milliseconds(0)
    .format(DATE_FORMAT.API_ISO);
};

const convertUtcDateTimeToLocalDateTime = (value: string) => {
  return moment.utc(value).local().seconds(0).milliseconds(0).format(DATE_FORMAT.API_ISO);
};

const convertLocalDateTimeToUtcDateTime = (value: string) => {
  return moment(value).utc().seconds(0).milliseconds(0).format(`${DATE_FORMAT.API_ISO}[Z]`);
};

const getCurrentDateTime = () => moment();

/**
 * Method for getting current date in the UTC (GMT +0)
 * @returns {moment.Moment}
 */
const getCurrentDateTimeInUtc = () => moment.utc();

const getTimeDifference = (startDate: moment.MomentInput, endDate = getCurrentDateTime()) => endDate.diff(startDate);

const isCurrentTimeInPeriod = (startDate: moment.MomentInput, endDate: moment.MomentInput) =>
  moment().isBetween(startDate, endDate);

const formatDateForAPI = (dateText: moment.MomentInput) => {
  return moment(dateText).format(DATE_FORMAT.API_ONLY_DATE);
};

const getMaxDate = (dates: moment.Moment[]) => moment.max(dates);

const getMinDate = (dates: moment.Moment[]) => moment.min(dates);

const subtractDaysFromStartDate = (
  startDate: string,
  daysToSubtract: moment.DurationInputArg1,
  dateFormat = DATE_FORMAT.SIMPLE,
) => {
  const momentStartDate = getMomentDateObj(startDate);
  const newDate = momentStartDate.subtract(daysToSubtract, 'days');

  return newDate.format(dateFormat);
};

const useDateService = () => {
  const { getUserTimezone } = useDateTimeService();

  const getToday = useCallback(() => momentTimezone.tz(moment(), getUserTimezone()) || moment(), [getUserTimezone]);

  return useMemo(
    () => ({
      formatDateForAPI,
      getMonthsBetween,
      formatDate,
      isIsoDate,
      getToday,
      getCurrentDate,
      getCurrentDateTime,
      getCurrentDateTimeInUtc,
      getTimeDifference,
      getStringDurationBetweenTwoDates,
      getMaxDate,
      getMinDate,
      getMomentDateObj,
      getMomentDateObjAsUtc,
      getDaysBetween,
      getHoursBetween,
      getMinutesBetween,
      getMinutesFromDateTime,
      formatMinutesAsTime,
      setTimeFromMinutes,
      convertUtcDateTimeToLocalDateTime,
      convertLocalDateTimeToUtcDateTime,
      getUserTimezone,
      subtractDaysFromStartDate,
      isCurrentTimeInPeriod,
      datesDifference,
      datesDifferenceExpanded,
    }),
    [getToday, getUserTimezone],
  );
};

export default useDateService;
export {
  isIsoDate,
  todayIsoDate,
  formatDate,
  getMinutesFromDateTime,
  setTimeFromMinutes,
  convertUtcDateTimeToLocalDateTime,
  convertLocalDateTimeToUtcDateTime,
};
